/**
 * Patient Edit Hooks
 *
 * React Query hooks for Patient Edit screen (患者編集)
 * Encapsulates clinic context automatically using AuthContext
 */

import {
  bulkCreatePatientHistory,
  createPatient,
  deleteHistory,
  deletePatient,
  fetchPatientEdit,
  fetchPatientLookupData,
  provisionalSavePatient,
  searchPatients as searchPatientsAPI,
  switchPatientHistory,
  updatePatient,
} from '@/pages/main/patient/services';
import {
  defaultPatientFormData,
  PatientFormData,
  PatientFormSchemaType,
  PatientLookupData,
} from '@/pages/main/patient/types';
import { useAuth } from '@/shared/providers/AuthContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useAppToast from '@hooks/useAppToast';

const generateDummyLookupData = (): PatientLookupData => ({
  sexs: [],
  relationships: [],
  insurance_types: [],
  patient_costs: [],
  elderly_insurance_certificates: [],
  insurer_types: [],
});

// ==================== API Service Functions ====================

// All API calls are now in @/pages/main/patient/services
// These hooks wrap them with React Query and clinic context

// ==================== Hooks ====================

/**
 * Hook to get patient edit data
 *
 * Automatically extracts siteType and clinicNumber from auth context
 */
export const useMainPatientEdit = (id?: number) => {
  const { clinic } = useAuth();
  const siteType = clinic?.site_type || 'medix';
  const clinicNumber = clinic?.clinic_number || '';

  return useQuery({
    queryKey: ['patients', siteType, clinicNumber, 'edit', id],
    queryFn: async () => {
      if (!id) {
        return defaultPatientFormData;
      }
      return fetchPatientEdit(siteType, clinicNumber, id.toString());
    },
    enabled: !!siteType && !!clinicNumber && !!id,
  });
};

/**
 * Hook to get lookup data for dropdowns
 * Fetches from /master-data API endpoint, falls back to hardcoded values
 */
export const useMainPatientLookupData = () => {
  const { clinic } = useAuth();
  const siteType = clinic?.site_type || 'medix';
  const clinicNumber = clinic?.clinic_number || '';

  return useQuery({
    queryKey: ['patients', siteType, clinicNumber, 'lookup'],
    queryFn: async () => {
      try {
        // Fetch actual master data from API
        const response = await fetchPatientLookupData(siteType, clinicNumber);

        if (!response.success) {
          throw new Error('Failed to fetch master data');
        }

        const data = response.data;

        // Map API response to PatientLookupData format
        return {
          sexs: data?.sexes || [],
          relationships: data?.relationships || [],
          insurance_types: data?.insurance_types || [],
          patient_costs: data?.patient_costs || [],
          elderly_insurance_certificates: data?.elderly_insurance_certificates || [],
          insurer_types: data?.insurer_types || [],
        };
      } catch (error) {
        console.warn('Failed to fetch master data from API, using fallback', error);
        // Fall back to hardcoded values if API fails
        return generateDummyLookupData();
      }
    },
    enabled: !!siteType && !!clinicNumber,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * Hook for patient edit mutations
 * Automatically handles clinic context
 */
export const useMainPatientEditMutation = () => {
  const { clinic } = useAuth();
  const queryClient = useQueryClient();
  const siteType = clinic?.site_type || 'medix';
  const clinicNumber = clinic?.clinic_number || '';
  const toast = useAppToast();

  /**
   * Create new patient mutation
   */
  const createMutation = useMutation({
    mutationFn: async (data: PatientFormSchemaType) => {
      return createPatient(siteType, clinicNumber, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['patients', siteType, clinicNumber],
      });
      toast.success('更新が正常に終了しました。');
    },
    onError: () => {
      toast.error('更新ができませんでした。');
    },
  });

  /**
   * Update existing patient mutation
   */
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string | number; data: PatientFormSchemaType }) => {
      return updatePatient(siteType, clinicNumber, id.toString(), data);
    },
    onSuccess: (_result: PatientFormData, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['patients', siteType, clinicNumber],
      });
      // Must match useMainPatientEdit queryKey last segment (numeric id from route/API).
      queryClient.invalidateQueries({
        queryKey: ['patients', siteType, clinicNumber, 'edit', Number(variables.id)],
      });
      toast?.success('更新が正常に終了しました。');
    },
    onError: () => {
      toast?.error('更新ができませんでした。', { bypassSuppression: true });
    },
  });

  /**
   * Provisional save mutation (仮登録)
   */
  const provisionalSaveMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id?: string | number | null;
      data: PatientFormSchemaType;
    }) => {
      return provisionalSavePatient(siteType, clinicNumber, id?.toString() || null, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['patients', siteType, clinicNumber],
      });
      toast?.success('仮登録が正常に終了しました。');
    },
    onError: () => {
      toast?.error('仮登録ができませんでした。');
    },
  });

  /**
   * Delete patient mutation
   * Idempotent: retrying a deletion is safe
   */
  const deleteMutation = useMutation({
    mutationFn: async (id: string | number) => {
      return deletePatient(siteType, clinicNumber, id.toString());
    },
    onSuccess: () => {
      // Only invalidate the patient list, not the specific patient edit query
      // to avoid 404 errors for deleted patient
      queryClient.removeQueries({
        queryKey: ['patient-edit', siteType, clinicNumber],
      });
    },
    onError: (error: any) => {
      // Log error for debugging but don't show generic toast
      // Error handling is done in the page component for better UX
      console.error('Patient deletion error:', error?.response?.data || error?.message);
    },
  });

  /**
   * Switch patient history mutation
   */
  const switchHistoryMutation = useMutation({
    mutationFn: async ({
      patientId,
      type,
      historyId,
    }: {
      patientId: number;
      type: 'patient_information' | 'health_insurance_card' | 'medical_aid';
      historyId: number;
    }) => {
      return switchPatientHistory(siteType, clinicNumber, patientId, type, historyId);
    },
    onSuccess: (_, variables) => {
      // Invalidate the patient edit query to refresh the data
      queryClient.invalidateQueries({
        queryKey: ['patients', siteType, clinicNumber, 'edit', variables.patientId],
      });
    },
    onError: () => {
      toast?.error('履歴の切り替えに失敗しました');
    },
  });

  /**
   * Delete patient history mutation
   * Idempotent: retrying a successful deletion will return success
   */
  const deleteHistoryMutation = useMutation({
    mutationFn: async ({
      patientId,
      type,
      historyId,
    }: {
      patientId: number;
      type: 'patient_information' | 'health_insurance_card' | 'medical_aid';
      historyId: number;
    }) => {
      return deleteHistory(siteType, clinicNumber, patientId, type, historyId);
    },
    onSuccess: (_, variables) => {
      // Invalidate the patient edit query to refresh the data
      queryClient.invalidateQueries({
        queryKey: ['patients', siteType, clinicNumber, 'edit', variables.patientId],
      });
      toast?.success('削除が正常に終了しました。');
    },
    onError: (error: any) => {
      // Extract error message from backend response
      // Handle both validation errors and generic errors
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.history?.[0] ||
        error?.message ||
        '履歴の削除に失敗しました';
      toast?.error(errorMessage);
    },
  });

  /**
   * Bulk create patient history mutation
   */
  const bulkCreateHistoryMutation = useMutation({
    mutationFn: async ({ patientId, data }: { patientId: number; data: any }) => {
      return bulkCreatePatientHistory(siteType, clinicNumber, patientId, data);
    },
    onSuccess: (_, variables) => {
      // Invalidate the patient edit query to refresh the data
      queryClient.invalidateQueries({
        queryKey: ['patients', siteType, clinicNumber, 'edit', variables.patientId],
      });
      toast?.success('更新が正常に終了しました。');
    },
    onError: () => {
      toast?.error('履歴の作成に失敗しました');
    },
  });

  return {
    createPatient: createMutation,
    updatePatient: updateMutation,
    provisionalSave: provisionalSaveMutation,
    deletePatient: deleteMutation,
    switchHistory: switchHistoryMutation,
    deleteHistory: deleteHistoryMutation,
    bulkCreateHistory: bulkCreateHistoryMutation,
  };
};

/**
 * Hook for patient search
 * Automatically handles clinic context
 */
export const useMainPatientSearch = (toast?: any) => {
  const { clinic } = useAuth();
  const siteType = clinic?.site_type || 'medix';
  const clinicNumber = clinic?.clinic_number || '';

  return useMutation({
    mutationFn: async ({
      keyword,
      includeExcluded = false,
      perPage = 20,
      sortField = 'name_kana',
      sortOrder = 'asc',
    }: {
      keyword: string;
      includeExcluded?: boolean;
      perPage?: number;
      sortField?: string;
      sortOrder?: 'asc' | 'desc';
    }) => {
      return searchPatientsAPI(
        siteType,
        clinicNumber,
        keyword,
        includeExcluded,
        1, // page
        perPage,
        sortField,
        sortOrder
      );
    },
    onError: () => {
      toast?.error('患者検索に失敗しました');
    },
  });
};
